import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/trackEvent";

export function EmailSignupWidget({ sticky = false }: { sticky?: boolean }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Use rate-limited edge function for newsletter signup
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { 
          email: email.trim().toLowerCase(), 
          first_name: name.trim() || null 
        }
      });

      if (error) {
        throw error;
      }

      const normalizedEmail = email.trim().toLowerCase();
      trackEvent('signup', { method: 'newsletter', source: 'widget' }, normalizedEmail);
      trackEvent('form_submit', { form_name: 'newsletter_signup' }, normalizedEmail);


      if (data?.message === 'already_subscribed') {
        toast.info("You're already subscribed!", {
          description: "Check your inbox for daily crypto tips",
        });
      } else if (data?.message === 'subscribed') {
        toast.success("Welcome to Sydneygotoman!", {
          description: "Check your email for the welcome guide",
        });
      } else if (data?.error) {
        toast.error(data.error);
      }

      setEmail("");
      setName("");
    } catch (error: any) {
      console.error('Signup error:', error);
      if (error?.message?.includes('429') || error?.status === 429) {
        toast.error("Too many attempts. Please try again later.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 text-primary-foreground ${sticky ? 'sticky top-24' : ''}`}>
      <div className="text-center mb-4">
        <div className="text-4xl mb-2">📧</div>
        <h3 className="text-xl font-bold mb-2">Get Daily Tips</h3>
        <p className="text-sm opacity-90">Daily AUD market briefs at 6am AEST • 2-minute reads</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input 
          type="text"
          placeholder="First name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white focus:bg-white text-gray-900 placeholder:text-gray-500 font-medium border-0 focus-visible:ring-white"
          required
          disabled={isLoading}
        />
        <Input 
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white focus:bg-white text-gray-900 placeholder:text-gray-500 font-medium border-0 focus-visible:ring-white"
          required
          disabled={isLoading}
        />
        <Button 
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary-foreground hover:bg-primary-foreground/90 text-primary font-bold py-3 rounded-xl transition-all transform hover:scale-105"
        >
          {isLoading ? "Subscribing..." : "Subscribe Free →"}
        </Button>
      </form>
      
      <div className="mt-4 flex items-center justify-center gap-2 text-xs opacity-80">
        <Lock className="w-4 h-4" />
        Unsubscribe anytime
      </div>
      
      <div className="mt-3 text-center text-xs opacity-80">
        Daily crypto tips for Aussie investors
      </div>
    </div>
  );
}
